import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Download, Printer, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, StatCard, Badge, Button } from "@/components/common";

const PRESETS = [["today", "Hari Ini"], ["week", "Minggu Ini"], ["month", "Bulan Ini"], ["custom", "Custom"]];
const CHANNELS = [["all", "Semua"], ["pos", "Kasir POS"], ["online", "Order WA / Online"]];

function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function rangeFor(preset) {
  const now = new Date();
  if (preset === "today") return { from: iso(now), to: iso(now) };
  if (preset === "week") { const d = new Date(now); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return { from: iso(d), to: iso(now) }; }
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}
function downloadCSV(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}
function printReport() {
  const node = document.querySelector(".print-report");
  if (!node) return;
  const heads = [...document.querySelectorAll('link[rel="stylesheet"], style')].map((n) => n.outerHTML).join("");
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Laporan Interaktif</title>${heads}<style>.no-print{display:none!important}@page{size:A4 landscape;margin:12mm}body{background:#fff;padding:12px}</style></head><body>${node.outerHTML}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 600);
}

export default function ProfitReport() {
  const { branch } = useAuth();
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState(rangeFor("month"));
  const [channel, setChannel] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => (preset === "custom" ? custom : rangeFor(preset)), [preset, custom]);
  const params = useMemo(() => ({ ...(branch ? { branch_id: branch.id } : {}), date_from: range.from, date_to: range.to, channel }), [branch, range, channel]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/reports/profitability", params).then((d) => alive && setData(d)).catch(() => alive && setData(null)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [params]);

  const s = data?.summary;

  return (
    <div className="print-report">
      <PageHeader title="Laporan Interaktif" subtitle={branch?.name || "Semua Unit"} icon={BarChart3}
        actions={<Button variant="outline" onClick={printReport} data-testid="report-print"><Printer size={15} /> Print / PDF</Button>} />

      <div className="flex flex-wrap items-center gap-2 mb-3 no-print">
        {PRESETS.map(([v, l]) => (
          <button key={v} onClick={() => setPreset(v)} data-testid={`preset-${v}`}
            className={`px-3 h-9 rounded-xl text-sm font-medium ${preset === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-9 px-2 rounded-lg border border-slate-300 text-sm" data-testid="report-from" />
            <span className="text-slate-400">—</span>
            <input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-9 px-2 rounded-lg border border-slate-300 text-sm" data-testid="report-to" />
          </div>
        )}
        <div className="w-px h-6 bg-slate-200 mx-1" />
        {CHANNELS.map(([v, l]) => (
          <button key={v} onClick={() => setChannel(v)} data-testid={`channel-${v}`}
            className={`px-3 h-9 rounded-xl text-sm font-medium ${channel === v ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <Loading /> : !s ? <p className="text-slate-400">Gagal memuat laporan.</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <StatCard label="Total Omzet" value={rupiah(s.omzet)} sub={`${s.order_count} transaksi`} tone="primary" testid="sum-omzet" />
            <StatCard label="Total Modal Terpakai" value={rupiah(s.modal)} testid="sum-modal" />
            <StatCard label="Beban Waste" value={rupiah(s.waste_loss)} tone="danger" testid="sum-waste" />
            <StatCard label="Laba Kotor" value={rupiah(s.laba_kotor)} sub={`Margin ${s.margin}%`} tone="success" testid="sum-laba" />
            <StatCard label="Laba Bersih (−Waste)" value={rupiah(s.net_profit)} testid="sum-net" />
          </div>

          <Card className="p-4 mb-6">
            <div className="text-sm font-medium text-slate-600 mb-2">Breakdown Metode Bayar</div>
            <div className="flex flex-wrap gap-2" data-testid="pay-breakdown">
              {s.payments.length ? s.payments.map((p) => (
                <Badge key={p.method} className="bg-slate-100 text-slate-700 text-sm px-3 py-1">{p.label}: {rupiah(p.amount)}</Badge>
              )) : <span className="text-slate-400 text-sm">Belum ada pembayaran</span>}
            </div>
          </Card>

          <ReportTable
            title="Laba per Produk (dari Pemakaian Rangkaian Aktual)"
            testid="table-products"
            onExport={() => downloadCSV(`laba_produk_${range.from}_${range.to}.csv`, ["Produk", "Qty Terjual", "Omzet", "Modal (HPP)", "HPP/unit", "Laba", "Margin %", "Order"],
              (data.products || []).map((p) => [p.name, p.qty, p.revenue, p.modal, p.hpp_unit, p.profit, p.margin, p.orders]))}
            head={["Produk", "Qty", "Omzet", "Modal (HPP)", "HPP/unit", "Laba", "Margin", "Order"]}
            rows={(data.products || []).map((p) => (
              <tr key={p.product_id} className="border-b border-slate-50" data-testid={`prodprofit-${p.product_id}`}>
                <td className="py-2 px-3 font-medium text-slate-700">{p.name}</td>
                <td className="py-2 px-3 text-right text-slate-600">{num(p.qty)}</td>
                <td className="py-2 px-3 text-right">{rupiah(p.revenue)}</td>
                <td className="py-2 px-3 text-right text-slate-500">{rupiah(p.modal)}</td>
                <td className="py-2 px-3 text-right text-slate-500">{rupiah(p.hpp_unit)}</td>
                <td className={`py-2 px-3 text-right font-semibold ${p.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{rupiah(p.profit)}</td>
                <td className="py-2 px-3 text-right text-slate-500">{p.margin}%</td>
                <td className="py-2 px-3 text-right text-slate-400">{p.orders}</td>
              </tr>
            ))}
            empty={!(data.products || []).length}
          />

          <ReportTable
            title="Transaksi & Margin Profit"
            testid="table-transactions"
            onExport={() => downloadCSV(`transaksi_${range.from}_${range.to}.csv`, ["No Order", "Tanggal", "Channel", "Customer", "Harga Jual", "Harga Modal", "Profit", "Margin %"],
              data.transactions.map((t) => [t.order_no, fmtDate(t.created_at), t.channel, t.customer, t.revenue, t.modal, t.profit, t.margin]))}
            head={["No Order", "Tanggal", "Channel", "Customer", "Harga Jual", "Modal", "Profit", "Margin"]}
            rows={data.transactions.map((t) => (
              <tr key={t.order_no} className="border-b border-slate-50" data-testid={`tx-${t.order_no}`}>
                <td className="py-2 px-3 font-medium text-slate-700">{t.order_no}</td>
                <td className="py-2 px-3 text-slate-400 text-xs">{fmtDate(t.created_at)}</td>
                <td className="py-2 px-3"><Badge className={t.channel === "POS" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}>{t.channel}</Badge></td>
                <td className="py-2 px-3 text-slate-600">{t.customer}</td>
                <td className="py-2 px-3 text-right">{rupiah(t.revenue)}</td>
                <td className="py-2 px-3 text-right text-slate-500">{t.has_modal ? rupiah(t.modal) : <span className="text-amber-500 text-xs">belum dicatat</span>}</td>
                <td className="py-2 px-3 text-right font-semibold text-emerald-600">{rupiah(t.profit)}</td>
                <td className="py-2 px-3 text-right text-slate-500">{t.margin}%</td>
              </tr>
            ))}
            empty={!data.transactions.length}
          />

          <ReportTable
            title="Stok & Waste Bahan Baku"
            testid="table-materials"
            onExport={() => downloadCSV(`stok_waste_${range.from}_${range.to}.csv`, ["Bahan", "Satuan", "Stok Awal", "Terpakai", "Qty Waste", "Nilai Waste", "Sisa", "Min", "Alert"],
              data.materials.map((m) => [m.name, m.unit, m.awal, m.terpakai, m.waste_qty, m.waste_loss, m.sisa, m.min_stock, m.low ? "TIPIS" : ""]))}
            head={["Bahan", "Satuan", "Stok Awal", "Terpakai", "Waste", "Nilai Waste", "Sisa", "Status"]}
            rows={data.materials.map((m) => (
              <tr key={m.item_id} className="border-b border-slate-50" data-testid={`mat-${m.item_id}`}>
                <td className="py-2 px-3 font-medium text-slate-700">{m.name}</td>
                <td className="py-2 px-3 text-slate-400 text-xs">{m.unit}</td>
                <td className="py-2 px-3 text-right">{num(m.awal)}</td>
                <td className="py-2 px-3 text-right text-slate-600">{num(m.terpakai)}</td>
                <td className="py-2 px-3 text-right">{m.waste_qty ? num(m.waste_qty) : "-"}</td>
                <td className="py-2 px-3 text-right text-rose-600">{m.waste_loss ? rupiah(m.waste_loss) : "-"}</td>
                <td className="py-2 px-3 text-right font-semibold">{num(m.sisa)}</td>
                <td className="py-2 px-3 text-right">{m.low ? <Badge className="bg-rose-100 text-rose-700"><AlertTriangle size={11} className="inline" /> Tipis</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Aman</Badge>}</td>
              </tr>
            ))}
            empty={!data.materials.length}
          />

          <ReportTable
            title="Kinerja Perangkai"
            testid="table-arrangers"
            onExport={() => downloadCSV(`kinerja_perangkai_${range.from}_${range.to}.csv`, ["Perangkai", "Jumlah Rangkaian", "Nilai Modal Dikelola"],
              data.arrangers.map((a) => [a.name, a.count, a.modal]))}
            head={["Perangkai", "Jumlah Rangkaian", "Nilai Modal Dikelola"]}
            rows={data.arrangers.map((a, i) => (
              <tr key={i} className="border-b border-slate-50" data-testid={`arr-${i}`}>
                <td className="py-2 px-3 font-medium text-slate-700">{a.name}</td>
                <td className="py-2 px-3 text-right">{num(a.count)}</td>
                <td className="py-2 px-3 text-right font-semibold text-primary">{rupiah(a.modal)}</td>
              </tr>
            ))}
            empty={!data.arrangers.length}
          />
        </>
      )}
    </div>
  );
}

function ReportTable({ title, head, rows, onExport, empty, testid }) {
  return (
    <Card className="overflow-hidden mb-6">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <button onClick={onExport} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium no-print" data-testid={`${testid}-export`}><Download size={14} /> Export CSV</button>
      </div>
      {empty ? <p className="px-4 py-8 text-center text-slate-400 text-sm">Belum ada data pada periode ini.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={testid}>
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              {head.map((h, i) => <th key={i} className={`py-2.5 px-3 font-medium ${i >= 4 ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
